import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 40, left: 140 };
const WIDTH = 650;
const HEIGHT = 400;

/**
 * WIP Aging chart — horizontal bar chart with color-coded WIP status.
 * Data format: dimensionValue = "listTitle|wipLimit|status", measureValue = cardCount.
 * Status encoding: under=green, at=yellow, over=red, no-limit=gray.
 */
export function WipAgingChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleBarInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({ dimensionValue: dimensionValue.split('|')[0] ?? dimensionValue, measureValue, dimensionLabel, measureLabel, x: targetRect.left - rect.left + targetRect.width / 2, y: targetRect.top - rect.top });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Parse encoded data
  const parsedData = React.useMemo(() => {
    return data.map((d) => {
      const parts = d.dimensionValue.split('|');
      const listTitle = parts[0] ?? d.dimensionValue;
      const wipLimit = parts[1] ? parseInt(parts[1], 10) : null;
      const status = (parts[2] ?? 'no-limit') as 'under' | 'at' | 'over' | 'no-limit';
      return { listTitle, wipLimit, status, cardCount: d.measureValue, raw: d.dimensionValue };
    });
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || parsedData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('class', 'chart-content').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const yScale = d3.scaleBand().domain(parsedData.map((d) => d.listTitle)).range([0, innerHeight]).padding(0.2);
    const xMax = Math.max(d3.max(parsedData, (d) => d.cardCount) ?? 0, d3.max(parsedData, (d) => d.wipLimit ?? 0) ?? 0);
    const xScale = d3.scaleLinear().domain([0, xMax * 1.1]).nice().range([0, innerWidth]);

    // Axes
    g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale)).attr('font-size', '12px');
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '11px');

    // Color mapping
    const statusColors: Record<string, string> = {
      under: '#16a34a',   // green-600
      at: '#ca8a04',      // yellow-600
      over: '#dc2626',    // red-600
      'no-limit': '#6b7280', // gray-500
    };

    // Bars
    parsedData.forEach((d) => {
      const color = statusColors[d.status] ?? '#6b7280';
      const y = yScale(d.listTitle) ?? 0;
      const barWidth = xScale(d.cardCount);
      const barHeight = yScale.bandwidth();

      g.append('rect')
        .attr('x', 0)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', color)
        .attr('rx', 3);

      // WIP limit reference line
      if (d.wipLimit !== null && d.wipLimit > 0) {
        const limitX = xScale(d.wipLimit);
        g.append('line')
          .attr('x1', limitX)
          .attr('y1', y - 2)
          .attr('x2', limitX)
          .attr('y2', y + barHeight + 2)
          .attr('stroke', '#1f2937')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2');
      }

      // Card count label
      g.append('text')
        .attr('x', barWidth + 4)
        .attr('y', y + barHeight / 2 + 4)
        .attr('font-size', '11px')
        .attr('fill', 'currentColor')
        .text(String(d.cardCount));
    });

    // Axis labels
    g.append('text').attr('x', innerWidth / 2).attr('y', innerHeight + MARGIN.bottom - 5).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(measureLabel || 'Card Count');
  }, [parsedData, measureLabel]);

  if (data.length === 0) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">No data available</div>;
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const xMax = Math.max(d3.max(parsedData, (d) => d.cardCount) ?? 0, d3.max(parsedData, (d) => d.wipLimit ?? 0) ?? 0);
  const yScale = d3.scaleBand().domain(parsedData.map((d) => d.listTitle)).range([0, innerHeight]).padding(0.2);
  const xScale = d3.scaleLinear().domain([0, xMax * 1.1]).nice().range([0, innerWidth]);

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`WIP aging chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full max-w-[650px]" aria-hidden="true"><ChartPatternDefs /></svg>
      {/* Legend */}
      <div className="mt-2 flex items-center justify-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-green-600" />Under</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-yellow-600" />At Limit</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-red-600" />Over</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm bg-gray-500" />No Limit</span>
        <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 border-t-2 border-dashed border-gray-800" />WIP Limit</span>
      </div>
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {parsedData.map((d) => {
          const barX = MARGIN.left / WIDTH * 100;
          const barY = (MARGIN.top + (yScale(d.listTitle) ?? 0)) / HEIGHT * 100;
          const barWidth = xScale(d.cardCount) / WIDTH * 100;
          const barHeight = yScale.bandwidth() / HEIGHT * 100;
          return (
            <button key={d.raw} type="button" role="listitem" className="absolute border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" style={{ left: `${barX}%`, top: `${barY}%`, width: `${Math.max(barWidth, 2)}%`, height: `${barHeight}%` }} tabIndex={0} aria-label={`${d.listTitle}: ${d.cardCount} cards, status: ${d.status}${d.wipLimit ? `, limit: ${d.wipLimit}` : ''}`} onMouseEnter={(e) => handleBarInteraction(d.raw, d.cardCount, e)} onFocus={(e) => handleBarInteraction(d.raw, d.cardCount, e)} onMouseLeave={hideTooltip} onBlur={hideTooltip} onClick={() => onSegmentClick?.(d.listTitle)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSegmentClick?.(d.listTitle); } }} />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
